import { useSite } from "@/contexts/SiteContext";
import SmartLink from "@/components/ui/SmartLink";
import { RenderIcon } from "@/components/admin/IconPicker";

// Fallback self-hosted (inline SVG, fără hotlink extern) pentru fundalul benzii
const BAND_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1600' height='1000'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%230D1B2A'/%3E%3Cstop offset='1' stop-color='%230E7C62'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23g)'/%3E%3C/svg%3E";

/**
 * „Investește în tine" — foto full-bleed sub overlay bleumarin + 3 carduri de preț
 * semi-opace cu icoane thin-line aurii și prețuri serif. Editabilă din /admin/sectiuni → invest_band.
 */
export default function InvestBand() {
  const { sectionContent } = useSite();
  const c = sectionContent("invest_band");
  if (!c) return null;
  const items = c.items || [];
  const image = c.image_url || BAND_FALLBACK;
  // Prețurile ședințelor sunt cerute explicit de client — se afișează
  // întotdeauna, independent de site.show_prices (care controlă doar
  // prețurile la cursuri/bibliotecă). Vezi IMPORTANT 2 din review.

  return (
    <section data-testid="invest-band-section" className="relative w-full overflow-hidden">
      {/* Foto full-bleed */}
      <img src={image} alt="" aria-hidden="true" data-img="image_url" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 40%" }} />
      {/* Overlay bleumarin — puternic la stânga (text), lasă fotografia să respire la dreapta-sus */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(90deg, rgba(7,19,31,0.97) 0%, rgba(7,19,31,0.88) 45%, rgba(7,19,31,0.45) 100%)" }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(13,27,42,0.9) 0%, rgba(13,27,42,0) 22%, rgba(13,27,42,0) 70%, rgba(13,27,42,0.92) 100%)" }}
      />

      <div className="relative max-w-[1440px] mx-auto px-4 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-[1fr_1.6fr] gap-10 lg:gap-14 items-center">
        {/* Text stânga */}
        <div>
          <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Programe premium"}</span>
          <h2 data-el="title" data-field="title" className="font-serif text-4xl sm:text-5xl leading-tight mt-3" style={{ color: "#F7F5F0" }}>
            {c.title || "Investește în tine"}
          </h2>
          {c.subtitle && (
            <p data-el="subtitle" data-field="subtitle" className="text-sm sm:text-base leading-relaxed mt-4 max-w-md" style={{ color: "var(--ct-text-muted)" }}>
              {c.subtitle}
            </p>
          )}
          <SmartLink to={c.cta_link || "/sedinte"} data-el="cta" className="btn-outline-gold mt-7" style={{ color: "var(--ct-gold)" }} data-testid="invest-band-cta">
            <span data-field="cta_text">{c.cta_text || "Descoperă programele"}</span>
          </SmartLink>
        </div>

        {/* Carduri preț — grid pe 2 coloane: cele 4 ședințe din seed formează un 2×2 echilibrat
            (înainte era grid-cols-3, care lăsa al 4-lea card singur pe un rând nou). */}
        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {items.map((it, i) => (
            <SmartLink
              key={i}
              to={it.link || c.cta_link || "/sedinte"}
              data-testid={`invest-card-${i}`}
              className="flex flex-col items-center text-center px-5 py-8 transition-transform hover:-translate-y-1"
              style={{
                background: "rgba(13, 27, 42, 0.82)",
                border: "1px solid var(--ct-border)",
                borderRadius: "4px",
                backdropFilter: "blur(6px)",
              }}
            >
              <RenderIcon value={it.icon || "Gem"} size={30} color="var(--ct-gold)" />
              <h3 data-el="item_title" data-field={`items.${i}.title`} className="font-serif uppercase tracking-[0.14em] text-sm sm:text-base mt-4" style={{ color: "#F7F5F0" }}>
                {it.title}
              </h3>
              <p data-el="item_body" data-field={`items.${i}.desc`} className="text-xs leading-relaxed mt-2 flex-1" style={{ color: "var(--ct-text-muted)" }}>
                {it.desc}
              </p>
              {it.duration && (
                <span data-el="item_body" data-field={`items.${i}.duration`} className="text-[11px] uppercase tracking-[0.16em] mt-3" style={{ color: "var(--ct-text-muted)" }}>
                  {it.duration}
                </span>
              )}
              {it.price && (
                <p className="mt-2">
                  <span data-el="item_body" data-field={`items.${i}.price_label`} className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--ct-text-muted)" }}>{it.price_label || "De la"} </span>
                  <span data-el="price" data-field={`items.${i}.price`} className="font-serif text-2xl ml-1" style={{ color: "var(--ct-gold-soft)" }}>{it.price}</span>
                </p>
              )}
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
}
